import { useState } from "react";
import type { NewProgram } from "@heraion/shared";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { DraftResumeDialog } from "../ProgramEditor/DraftResumeDialog";
import { editorDraftKey } from "../ProgramEditor/editorDraftStore";
import { useDraftResume } from "../ProgramEditor/useDraftResume";

import { ComposerBody } from "./ProgramComposerBody";
import { NewProgramStart } from "./NewProgramStart";
import { PlanComposer } from "./PlanComposer";
import type { ProgramComposerProps } from "./types";

// Mounts: program = ProgramEditorV3 (through ComposerBody); plan = PlanComposer.
//
// Create-from-scratch: with neither `program` nor `creationSeed` the
// composer opens on the NewProgramStart stage (a model's wizard, a public
// template, or from scratch, on every host since slice 6) and seeds itself
// from it. Single
// mode (Plan) is delegated to PlanComposer.
//
// #336: the device draft is read HERE, once, before either editor mounts,
// so the editor seeds from it synchronously. A never-saved draft asks first
// (Resume / Start over); resumed, a program skips the setup stage it already
// answered.
export function ProgramComposer(props: ProgramComposerProps) {
  const [seed, setSeed] = useState<NewProgram | null>(null);
  const single = props.mode === "single";
  const existingId = (single ? props.plan?.id : props.program?.id) ?? null;
  // A host-given creation seed is a fresh start: it does not resume an
  // unrelated «new» draft. #375: a plan seed with a source («Plan again»)
  // reads the draft kept under that source, and asks like «new» does.
  const seedSource = existingId === null ? (props.planSeed?.source ?? null) : null;
  const readKey =
    props.draftScope &&
    !(existingId === null && (props.creationSeed || (props.planSeed && seedSource === null)))
      ? editorDraftKey(
          props.draftScope,
          single ? "plan" : "program",
          props.authoredAs ?? "athlete",
          existingId ?? seedSource,
        )
      : null;
  const { ready, stored, newDraft } = useDraftResume(readKey, existingId === null);
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  if (newDraft) {
    return (
      <DraftResumeDialog
        theme={resolveTheme(props.dark, family)}
        kind="newDraft"
        visible
        savedAtMs={newDraft.savedAtMs}
        onKeep={newDraft.onResume}
        onDrop={newDraft.onStartOver}
      />
    );
  }
  if (!ready) return null;
  if (single) return <PlanComposer {...props} stored={stored} />;
  // Resume: `stored` wins and the model falls (the athlete chose the draft).
  // Start over: `stored` is null, so the wizard opens on `initialModel`.
  if (!props.program && !props.creationSeed && !seed && !stored) {
    // #336 slice 5, #351: a new program starts from a model, a template or
    // scratch; slice 6: on the web too, one scrolling column at any width.
    return (
      <NewProgramStart
        dark={props.dark}
        apiFetch={props.apiFetch}
        catalog={props.exerciseCatalog}
        effortScale={props.profile?.effortScale ?? "rir"}
        confirm={props.confirm}
        durableWrite={props.durableWrite}
        authoredAs={props.authoredAs}
        initialModel={props.initialModel}
        onOpenLibrary={props.onOpenLibrary}
        onCreate={setSeed}
        onCancel={props.onCancel}
      />
    );
  }
  return (
    <ComposerBody
      {...props}
      creationSeed={props.creationSeed ?? seed ?? undefined}
      hostSeed={props.creationSeed !== undefined}
      stored={stored}
    />
  );
}

